import type { WithOptional } from "@utils/formUtils";
import type { GaVueComponent } from "@/common/vueUtils";
import type { DeepReadonly } from "vue";
import type { Store } from "@/common/storeUtils";
import { GaFormFieldLabel } from "@/components/form/GaFormField";

export type RadioOption<T extends string> = {
  label: string;
  value: T;
};

type Props<T extends string> = {
  value: T;
  options: RadioOption<T>[];
  onUpdateValue: (newValue: T) => void;
  label?: string;
  name: string;
  help?: string;
  disabled: boolean;
  required: boolean;
  errors?: DeepReadonly<string[]>;
  inline?: boolean;
};

type PropsWithOptional<T extends string> = WithOptional<Props<T>, "disabled" | "required" | "inline">;

function addDefaults<T extends string>(props: PropsWithOptional<T>): Props<T> {
  return {
    ...props,
    disabled: props.disabled ?? false,
    required: props.required ?? false,
    inline: props.inline ?? false,
  };
}

export function GaFormFieldInputRadioGroup<T extends string>(p: PropsWithOptional<T>): GaVueComponent {
  const props = addDefaults(p);
  const errors = props.errors ?? [];

  return (
    <div>
      {props.label !== undefined && props.label.trim().length > 0 ? (
        <label for={props.name} class="form-label">
          {props.label} {props.required ? <span>*</span> : null}
        </label>
      ) : null}
      <div>
        {props.options.map((option, index) => (
          <div
            key={index}
            class={{
              "form-check": true,
              "form-check-inline": props.inline,
              "is-invalid": errors.length > 0,
            }}>
            <input
              class={{
                "form-check-input": true,
                "is-invalid": errors.length > 0,
              }}
              type="radio"
              value={option.value}
              onInput={() => props.onUpdateValue(option.value)}
              name={props.name}
              id={props.name + index}
              required={props.required}
              disabled={props.disabled}
              checked={props.value === option.value}
            />
            <label for={props.name + index} class="form-check-label">
              {option.label}
            </label>
          </div>
        ))}
        {errors.map((error) => (
          <div class="invalid-feedback">{error}</div>
        ))}
        {props.help !== undefined && props.help.trim().length > 0 ? <div class="form-text">{props.help}</div> : null}
      </div>
    </div>
  );
}

type ExperimentalProps<T extends string> = {
  store: Store<T | null>;
  options: RadioOption<T>[];
  onBlur?: () => void;
  label?: string | GaVueComponent;
  name: string;
  help?: string;
  disabled?: boolean;
  required?: boolean;
  errors?: DeepReadonly<string[]>;
  inline?: boolean;
};

export function GaFormFieldInputRadioGroupExperimental<T extends string>(props: ExperimentalProps<T>): GaVueComponent {
  const errors = props.errors ?? [];
  const disabled = props.disabled ?? false;
  const required = props.required ?? false;
  const inline = props.inline ?? false;

  return (
    <div>
      <GaFormFieldLabel name={props.name} label={props.label} required={required} />
      <div>
        {props.options.map((option, index) => (
          <div
            key={index}
            class={{
              "form-check": true,
              "form-check-inline": inline,
              "is-invalid": errors.length > 0,
            }}>
            <input
              class={{
                "form-check-input": true,
                "is-invalid": errors.length > 0,
              }}
              type="radio"
              value={option.value}
              onInput={() => {
                props.store.set(option.value);
                props.onBlur?.();
              }}
              onBlur={(e) => {
                const nextFocused = e.relatedTarget;
                if (!(nextFocused instanceof HTMLInputElement) || nextFocused.name !== props.name) {
                  props.onBlur?.();
                }
              }}
              name={props.name}
              id={props.name + index}
              required={required}
              disabled={disabled}
              checked={props.store.get() === option.value}
            />
            <label for={props.name + index} class="form-check-label">
              {option.label}
            </label>
          </div>
        ))}
        {errors.map((error) => (
          <div class="invalid-feedback">{error}</div>
        ))}
        {props.help !== undefined && props.help.trim().length > 0 ? <div class="form-text">{props.help}</div> : null}
      </div>
    </div>
  );
}
